import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { fetchExternalOffers } from "../../discounts";
import { useDealEligibleItems } from "../../discounts/hooks/useDealEligibleItems";
import { isExternalCouponProvider } from "../../discounts/provider";
import { selectCatalogDiscounts } from "../../discounts/selectors";
import type { DiscountOffer } from "../../discounts/types";
import { groupMilestonesByStatus } from "../rewards";
import type { CustomerRewards } from "../rewards";
import {
  selectActiveLoyaltyProvider,
  selectCustomerLoyaltyInfo,
  selectCustomerRecordKey,
  selectIsOffline,
} from "../selectors";

/** A stable "none yet", so an unchanged render does not rebuild the groups. */
const NO_OFFERS: DiscountOffer[] = [];

export interface CustomerRewardsOptions {
  /**
   * `false` skips the partner-offers fetch. The groups are then built from the catalogue's copy of
   * each deal alone, which cannot say a reward is paused — so gate it on the rewards being SHOWN,
   * not on whether they exist.
   */
  enabled?: boolean;
}

/**
 * The customer's milestone rewards, each paired with its deal and grouped the way the customer
 * summary's Loyalty tab lists them. Two readers, so they cannot disagree about what "unclaimed"
 * means (DM-146, 2026-09-21): that tab, and the web cart's customer chip, whose "N rewards
 * available" is `unclaimed.length`.
 *
 * The deal comes from the partner's OFFERS first and the branch catalogue second — see
 * `groupMilestonesByStatus` for why only the offer can say a reward is paused.
 *
 * ⚠️ **Not `useLoyaltyRewards`.** That one draws the DEALS SHEET's rows against a bill: narrowed to
 * the sheet's visible deals, kept while on the bill, dropped when the deal is not a simple offer.
 * This one describes where the customer stands with no bill in play.
 *
 * The fetch is `fetchExternalOffers` — the one `useOfferCatalog` makes, normalised the same way —
 * and it is made only when there is something to pair and someone to ask: `enabled`, online (the
 * offers API is server-backed, and a doomed request still raises mobile's global loader — the
 * reason `useEnsureProgramDetails` skips offline too), the customer has milestones, and the
 * provider has an offers leg. It repeats when the provider or the customer changes, and a
 * response that lands after its request stopped being current is dropped.
 */
export function useCustomerRewards({ enabled = true }: CustomerRewardsOptions = {}): CustomerRewards {
  const customerLoyaltyInfo = useSelector(selectCustomerLoyaltyInfo);
  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const customerKey = useSelector(selectCustomerRecordKey);
  const isOffline = useSelector(selectIsOffline);
  const catalogDiscounts = useSelector(selectCatalogDiscounts);
  const getEligibleItems = useDealEligibleItems();
  const [offers, setOffers] = useState<DiscountOffer[]>(NO_OFFERS);

  const milestones = customerLoyaltyInfo?.milestones;
  const currentPoints = customerLoyaltyInfo?.redeemablePoints || 0;
  const hasMilestones = Array.isArray(milestones) && milestones.length > 0;
  const shouldFetch =
    enabled && !isOffline && hasMilestones && isExternalCouponProvider(activeProvider?.metaId);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let isCurrent = true;
    fetchExternalOffers(activeProvider, (next) => {
      if (isCurrent) setOffers(next);
    });
    return () => {
      isCurrent = false;
    };
    // `customerKey` is read by nothing inside: it is here so a customer swap refetches.
  }, [shouldFetch, activeProvider, customerKey]);

  return useMemo(
    () => ({
      ...groupMilestonesByStatus(milestones, currentPoints, {
        dealSources: [offers, Object.values(catalogDiscounts)],
        getEligibleItems,
      }),
      currentPoints,
    }),
    [milestones, currentPoints, offers, catalogDiscounts, getEligibleItems],
  );
}
